import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ApiHttp } from 'app/providers/api.http';
import { SlotFilterEnum } from 'app/models/enums/slot-filter.enum';

export interface GetAdminConnexRouteSlotsRequest {
	SearchName: string;
	Skip: number;
	Take: number;
	SortField: string;
	SortOrder: number;
	HeaderColumns: string[];
	DataColumns: string[];
	/** Column filters (AND). Empty / omitted = ignored. PascalCase matches API JSON (PropertyNamingPolicy null). */
	RouteNameFilter?: string | null;
	DirectionFilter?: string | null;
	PickupZoneNameFilter?: string | null;
	DropoffZoneNameFilter?: string | null;
	SlotFilter?: string | null;
	MaxPassengersFilter?: string | null;
	BookingWindowMinutesFilter?: string | null;
	PremiumFareAmountFilter?: string | null;
	StatusFilter?: string | null;
	UpdatedAtFilter?: string | null;
}

/** One row per ConneX route in the Route Configuration grid (aggregates slots). */
export interface AdminConnexRouteConfigurationRow {
	RouteId: number;
	RouteName: string;
	DirectionCount: number;
	PickupZonesSummary: string;
	DropoffZonesSummary: string;
	SlotCount: number;
	FareRangeMin: number;
	FareRangeMax: number;
	MaxPassengersPerVehicle: number;
	BookingWindowMinMinutes: number;
	BookingWindowMaxMinutes: number;
	PremiumFareMin: number;
	PremiumFareMax: number;
	Status: string;
	PublishedStateLabel: string;
	LastUpdatedAt?: string | null;
	LastUpdatedByName: string;
}

export interface GetAdminConnexRouteSlotsResponse {
	Success: boolean;
	TotalRecords: number;
	Items: AdminConnexRouteConfigurationRow[];
}

export const emptyBaseAdminRequest = () => ({
	SearchName: '',
	Skip: 0,
	Take: 0,
	SortField: '',
	SortOrder: 0,
	HeaderColumns: [] as string[],
	DataColumns: [] as string[]
});

/** Значения совпадают с <c>Xpress.DB.Enums.ConnexRouteStatus</c>. */
export enum ConnexRouteStatus {
	Draft = 0,
	Active = 1,
	Inactive = 2
}

export const parseConnexRouteStatusFromApi = (raw: string | undefined | null): ConnexRouteStatus | null => {
	const n = (raw ?? '').trim().toLowerCase();
	switch (n) {
		case 'draft':
			return ConnexRouteStatus.Draft;
		case 'active':
			return ConnexRouteStatus.Active;
		case 'inactive':
			return ConnexRouteStatus.Inactive;
		default:
			return null;
	}
};

export const connexRouteStatusToDisplayName = (status: ConnexRouteStatus): string => {
	switch (status) {
		case ConnexRouteStatus.Draft:
			return 'Draft';
		case ConnexRouteStatus.Active:
			return 'Active';
		case ConnexRouteStatus.Inactive:
			return 'Inactive';
	}
};

export interface AdminConnexZoneOption {
	Id: number;
	Name: string;
	Address: string;
}

export interface GetAdminConnexZonesResponse {
	Success: boolean;
	ErrorMessage?: string;
	Items: AdminConnexZoneOption[];
}

export interface ConnexRouteForBookingsOptionDto {
	RouteId: number;
	Name: string;
}

export interface GetConnexRoutesForBookingsResponse {
	Success: boolean;
	ErrorMessage?: string;
	Routes: ConnexRouteForBookingsOptionDto[];
}

export interface GetConnexSlotsForBookingsRequest {
	RouteId?: number | null;
	PickupZoneId?: number | null;
	DropoffZoneId?: number | null;
	DepartureDate?: string | null;
	EnforceBookingWindow?: boolean;
}

export interface ConnexSlotOptionDto {
	RouteId: number;
	SlotId: number;
	PickupZoneId: number;
	DropoffZoneId: number;
	PickupZoneName: string;
	DropoffZoneName: string;
	DepartureAt: string;
	FareAmount: number;
	TollAmount: number;
	PremiumFareAmount: number;
	TotalAmount: number;
	WalkInImmediateDispatchAmount: number;
	VehicleCapacity: number;
	MaxPassengersPerVehicle: number;
	IsAvailable: boolean;
	IsBookingWindowSatisfied: boolean;
}

export interface GetConnexSlotsForBookingsResponse {
	Success: boolean;
	ErrorMessage?: string;
	Slots: ConnexSlotOptionDto[];
}

export interface ConnexWalkInCustomerLookupDto {
	XpressUserId: number;
	UserName: string;
	Phone: string;
}

export interface LookupConnexWalkInCustomerResponse {
	Success: boolean;
	ErrorMessage?: string;
	Customers: ConnexWalkInCustomerLookupDto[];
}

export interface PlaceConnexWalkInDeliveryOrderRequest {
	BookingType: number;
	SlotId: number;
	DepartureDate: string;
	ExpectedAmount: number;
	PickupZoneId: number;
	DropoffZoneId: number;
	PaxCount: number;
	XpressUserId?: number | null;
	GuestName: string;
	GuestPhone: string;
	RiderInstructions: string;
	IsCashCollected: boolean;
}

export interface PlaceDeliveryOrderResponse {
	Success: boolean;
	ErrorMessage?: string;
	ErrorCode?: number;
	Id?: number;
}

export interface AdminConnexSlotAssignedDriverDto {
	DisplayName: string;
}

export interface AdminConnexRouteSlotEditDto {
	SlotId: number;
	PickupZoneId: number;
	DropoffZoneId: number;
	DepartureTime: string;
	FareAmount: number;
	TollAmount: number;
	PremiumFareAmount: number;
	VehicleCapacity: number;
	BookingWindowMinutes: number;
	SlotEnabled: boolean;
	AssignedDrivers?: AdminConnexSlotAssignedDriverDto[];
}

export interface GetAdminConnexRouteForEditResponse {
	Success: boolean;
	ErrorMessage?: string;
	RouteId: number;
	Name: string;
	RouteStatus: string;
	CustomersCanBook: boolean;
	DefaultBookingWindowMinutes: number;
	LastUpdatedAt?: string | null;
	LastUpdatedByName: string;
	PickupZoneIds: number[];
	DropoffZoneIds: number[];
	Slots: AdminConnexRouteSlotEditDto[];
}

export interface GetAdminConnexRouteForEditRequest {
	RouteId: number;
	SearchName: string;
	Skip: number;
	Take: number;
	SortField: string;
	SortOrder: number;
	HeaderColumns: string[];
	DataColumns: string[];
}

export interface SaveAdminConnexRouteSlotDto {
	SlotId: number;
	PickupZoneId: number;
	DropoffZoneId: number;
	DepartureTime: string;
	FareAmount: number;
	TollAmount: number;
	PremiumFareAmount: number;
	VehicleCapacity: number;
	BookingWindowMinutes: number;
	SlotEnabled: boolean;
}

export interface SaveAdminConnexRouteRequest {
	RouteId?: number | null;
	Name: string;
	SaveAsDraft: boolean;
	CustomersCanBook: boolean;
	PickupZoneIds: number[];
	DropoffZoneIds: number[];
	Slots: SaveAdminConnexRouteSlotDto[];
	SearchName: string;
	Skip: number;
	Take: number;
	SortField: string;
	SortOrder: number;
	HeaderColumns: string[];
	DataColumns: string[];
}

export interface SaveAdminConnexRouteResponse {
	Success: boolean;
	ErrorMessage?: string;
	RouteId: number;
}

export interface SetConnexRouteActiveStateRequest {
	SearchName: string;
	Skip: number;
	Take: number;
	SortField: string;
	SortOrder: number;
	HeaderColumns: string[];
	DataColumns: string[];
	RouteId: number;
	SetInactive: boolean;
}

export interface GetAdminConnexDriverAssignmentsRequest {
	ServiceDate: string;
	RouteId?: number | null;
	RouteStatusFilter?: DriverAssignmentRouteStatusFilterEnum | null;
	StatusFilter: DriverAssignmentStatusFilterEnum;
	SlotFilter: SlotFilterEnum;
	SearchName: string;
	Skip: number;
	Take: number;
	SortField: string;
	SortOrder: number;
	HeaderColumns: string[];
	DataColumns: string[];
}

export interface AdminConnexDriverAssignmentStats {
	TotalSlots: number;
	Ready: number;
	NoDrivers: number;
	DriversStaffed: number;
	BookedCount: number;
	CapacityCount: number;
}

export interface AdminConnexDriverAssignmentRouteOption {
	RouteId: number;
	RouteName: string;
}

export interface AdminConnexDriverAssignmentAssignedDriver {
	Name: string;
	PlateNumber: string;
}

export enum DriverStaffingStatusEnum {
	NoDriversAssigned = 1,
	PartiallyStaffed = 2,
	Ready = 3
}

export enum DriverAssignmentStatusFilterEnum {
	All = 1,
	NoDriversAssigned = 2,
	PartiallyStaffed = 3,
	FullyBooked = 4,
	Ready = 5,
	DriversStaffed = 6,
	SlotDisabled = 7
}

export enum DriverAssignmentRouteStatusFilterEnum {
	ActiveRoute = 1,
	InactiveRoute = 2
}

export interface AdminConnexDriverAssignmentSlot {
	SlotId: number;
	RouteId: number;
	RouteName: string;
	DepartureAt: string;
	PickupZoneName: string;
	DropoffZoneName: string;
	FareAmount: number;
	TollAmount: number;
	BookingWindowMinutes: number;
	RequiredDriverCount: number;
	AssignedDriverCount: number;
	BookedCount: number;
	CapacityCount: number;
	StaffingStatus: DriverStaffingStatusEnum;
	IsFullyBooked: boolean;
	ShowNotBookableWarning: boolean;
	AssignedDrivers: AdminConnexDriverAssignmentAssignedDriver[];
}

export interface GetAdminConnexDriverAssignmentsResponse {
	Success: boolean;
	Stats: AdminConnexDriverAssignmentStats;
	RouteOptions: AdminConnexDriverAssignmentRouteOption[];
	Items: AdminConnexDriverAssignmentSlot[];
}

export interface GetAdminConnexSlotDriverAssignmentsRequest {
	SlotId: number;
	ServiceDate: string;
	SearchName: string;
	Skip: number;
	Take: number;
	SortField: string;
	SortOrder: number;
	HeaderColumns: string[];
	DataColumns: string[];
}

export interface AdminConnexSlotDriverCandidate {
	XpressRiderId: number;
	Name: string;
	VehicleModel: string;
	Mobile: string;
	PlateNumber: string;
	ProfileImageUrl: string;
	AvailabilityStatus: ConnexDriverAvailabilityStatusEnum;
	IsAssigned: boolean;
	HasOverlap: boolean;
	OverlapMessage?: string | null;
}

export enum ConnexDriverAvailabilityStatusEnum {
	Available = 1,
	Assigned = 2,
	Unavailable = 3
}

export interface GetAdminConnexSlotDriverAssignmentsResponse {
	Success: boolean;
	Items: AdminConnexSlotDriverCandidate[];
}

export interface SaveAdminConnexSlotDriverAssignmentsRequest {
	SlotId: number;
	ServiceDate: string;
	XpressRiderIds: number[];
	SearchName: string;
	Skip: number;
	Take: number;
	SortField: string;
	SortOrder: number;
	HeaderColumns: string[];
	DataColumns: string[];
}

export interface SaveAdminConnexSlotDriverAssignmentsResponse {
	Success: boolean;
	ErrorMessage?: string;
	AssignedDriverCount: number;
}

@Injectable()
export class AdminXpressConnexService {
	constructor(private apiHttp: ApiHttp) {}

	getConnexRouteSlots(
		body: GetAdminConnexRouteSlotsRequest,
		messageService?: MessageService
	): Promise<GetAdminConnexRouteSlotsResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/GetConnexRouteSlots',
			body,
			messageService
		) as Promise<GetAdminConnexRouteSlotsResponse>;
	}

	getConnexZones(messageService?: MessageService): Promise<GetAdminConnexZonesResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/GetConnexZones',
			emptyBaseAdminRequest(),
			messageService
		) as Promise<GetAdminConnexZonesResponse>;
	}

	getConnexZonesForBookings(messageService?: MessageService): Promise<GetAdminConnexZonesResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/GetConnexZonesForBookings',
			emptyBaseAdminRequest(),
			messageService
		) as Promise<GetAdminConnexZonesResponse>;
	}

	getConnexRoutesForBookings(
		messageService?: MessageService
	): Promise<GetConnexRoutesForBookingsResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/GetConnexRoutesForBookings',
			emptyBaseAdminRequest(),
			messageService
		) as Promise<GetConnexRoutesForBookingsResponse>;
	}

	getConnexSlotsForBookings(
		body: GetConnexSlotsForBookingsRequest,
		messageService?: MessageService
	): Promise<GetConnexSlotsForBookingsResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/GetConnexSlotsForBookings',
			body,
			messageService
		) as Promise<GetConnexSlotsForBookingsResponse>;
	}

	lookupConnexWalkInCustomer(
		phone: string,
		messageService?: MessageService
	): Promise<LookupConnexWalkInCustomerResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/LookupConnexWalkInCustomer',
			{ Phone: phone },
			messageService
		) as Promise<LookupConnexWalkInCustomerResponse>;
	}

	placeConnexWalkInDeliveryOrder(
		body: PlaceConnexWalkInDeliveryOrderRequest,
		messageService?: MessageService
	): Promise<PlaceDeliveryOrderResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/PlaceConnexWalkInDeliveryOrder',
			body,
			messageService
		) as Promise<PlaceDeliveryOrderResponse>;
	}

	getConnexRouteForEdit(
		routeId: number,
		messageService?: MessageService
	): Promise<GetAdminConnexRouteForEditResponse> {
		const body: GetAdminConnexRouteForEditRequest = {
			...emptyBaseAdminRequest(),
			RouteId: routeId
		};
		return this.apiHttp.post(
			'api/AdminXpressConnex/GetConnexRouteForEdit',
			body,
			messageService
		) as Promise<GetAdminConnexRouteForEditResponse>;
	}

	createConnexRoute(
		body: SaveAdminConnexRouteRequest,
		messageService?: MessageService
	): Promise<SaveAdminConnexRouteResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/CreateConnexRoute',
			body,
			messageService
		) as Promise<SaveAdminConnexRouteResponse>;
	}

	updateConnexRoute(
		body: SaveAdminConnexRouteRequest,
		messageService?: MessageService
	): Promise<SaveAdminConnexRouteResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/UpdateConnexRoute',
			body,
			messageService
		) as Promise<SaveAdminConnexRouteResponse>;
	}

	setConnexRouteActiveState(
		body: SetConnexRouteActiveStateRequest,
		messageService?: MessageService
	): Promise<SaveAdminConnexRouteResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/SetConnexRouteActiveState',
			body,
			messageService
		) as Promise<SaveAdminConnexRouteResponse>;
	}

	getConnexDriverAssignments(
		body: GetAdminConnexDriverAssignmentsRequest,
		messageService?: MessageService
	): Promise<GetAdminConnexDriverAssignmentsResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/GetConnexDriverAssignments',
			body,
			messageService
		) as Promise<GetAdminConnexDriverAssignmentsResponse>;
	}

	getConnexSlotDriverAssignments(
		body: GetAdminConnexSlotDriverAssignmentsRequest,
		messageService?: MessageService
	): Promise<GetAdminConnexSlotDriverAssignmentsResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/GetConnexSlotDriverAssignments',
			body,
			messageService
		) as Promise<GetAdminConnexSlotDriverAssignmentsResponse>;
	}

	saveConnexSlotDriverAssignments(
		body: SaveAdminConnexSlotDriverAssignmentsRequest,
		messageService?: MessageService
	): Promise<SaveAdminConnexSlotDriverAssignmentsResponse> {
		return this.apiHttp.post(
			'api/AdminXpressConnex/SaveConnexSlotDriverAssignments',
			body,
			messageService
		) as Promise<SaveAdminConnexSlotDriverAssignmentsResponse>;
	}
}
